import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MessagesSquare, Plus, Send, Users as UsersIcon, Search, Paperclip, FileText, Loader2 } from 'lucide-react';
import api, { API } from '../lib/api';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';

const inputCls = 'w-full bg-muted/40 border border-border rounded-lg px-3.5 py-2.5 text-sm outline-none focus:border-primary transition-colors placeholder:text-muted-foreground/50';

const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  return sameDay ? time : `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')} ${time}`;
};

const Avatar = ({ name, online, size = 40 }) => (
  <div className="relative shrink-0">
    <img src={`https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'U')}&background=2563eb&color=fff`} alt="" className="rounded-full" style={{ width: size, height: size }} />
    {online !== undefined && <span className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-[#0b0b0d] ${online ? 'bg-emerald-400' : 'bg-foreground/25'}`} />}
  </div>
);

const Chat = () => {
  const { user: me } = useAuth();
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [search, setSearch] = useState('');
  const [newOpen, setNewOpen] = useState(false);
  const [mode, setMode] = useState('dm');
  const [allUsers, setAllUsers] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [selected, setSelected] = useState([]);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const bottomRef = useRef(null);
  const fileRef = useRef(null);
  const activeRef = useRef(null);
  activeRef.current = active;

  const loadConvs = useCallback(async () => {
    try { const { data } = await api.get('/chat/conversations'); setConvs(data); } catch (e) {}
  }, []);

  const loadMessages = useCallback(async (cid, scroll = false) => {
    try {
      const { data } = await api.get(`/chat/conversations/${cid}/messages`);
      setMessages((prev) => (JSON.stringify(prev) === JSON.stringify(data) ? prev : data));
      if (scroll) setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'auto' }), 60);
    } catch (e) {}
  }, []);

  useEffect(() => {
    loadConvs();
    const iv = setInterval(loadConvs, 5000);
    return () => clearInterval(iv);
  }, [loadConvs]);

  useEffect(() => {
    if (!active) return;
    loadMessages(active.id, true);
    const iv = setInterval(() => loadMessages(activeRef.current?.id), 3000);
    return () => clearInterval(iv);
  }, [active?.id, loadMessages]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages.length]);

  const openNew = async (m) => {
    setMode(m); setNewOpen(true); setSelected([]); setGroupName('');
    try { const { data } = await api.get('/users'); setAllUsers(data.filter((u) => u.user_id !== me?.user_id)); } catch (e) {}
  };

  const createConv = async (memberIds, type, name = '') => {
    try {
      const { data } = await api.post('/chat/conversations', { type, member_ids: memberIds, name });
      setNewOpen(false);
      await loadConvs();
      setActive({ id: data.id, type: data.type, title: data.name || allUsers.find((u) => u.user_id === memberIds[0])?.name || '' });
    } catch (e) {
      const d = e.response?.data?.detail;
      toast({ title: 'Erro', description: typeof d === 'string' ? d : 'Tente novamente.' });
    }
  };

  const send = async () => {
    const t = text.trim();
    if (!t || !active || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/chat/conversations/${active.id}/messages`, { text: t });
      setText('');
      setMessages((p) => [...p, data]);
      loadConvs();
    } catch (e) { toast({ title: 'Erro ao enviar mensagem' }); }
    finally { setSending(false); }
  };

  const sendFiles = async (files) => {
    if (!files?.length || !active) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append('file', f);
        const { data } = await api.post(`/chat/conversations/${active.id}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setMessages((p) => [...p, data]);
      }
      loadConvs();
    } catch (e) { toast({ title: 'Erro ao enviar arquivo', description: e.response?.data?.detail || '' }); }
    finally { setUploading(false); }
  };

  const filtered = convs.filter((c) => c.title.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="h-[calc(100vh-140px)] flex rounded-xl border border-border/70 bg-card overflow-hidden" data-testid="chat-page">
      {/* Lista de conversas */}
      <div className={`w-full md:w-[340px] md:border-r border-border/70 flex flex-col ${active ? 'hidden md:flex' : 'flex'}`}>
        <div className="p-4 border-b border-border/70 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-display font-black text-lg uppercase tracking-wide">Conversas</h2>
            <div className="flex gap-2">
              <button data-testid="new-dm-btn" onClick={() => openNew('dm')} title="Nova conversa" className="p-2 rounded-lg bg-primary hover:bg-primary/90 transition-colors"><Plus className="w-4 h-4" /></button>
              <button data-testid="new-group-btn" onClick={() => openNew('group')} title="Novo grupo" className="p-2 rounded-lg bg-muted/50 border border-border hover:bg-muted/80 transition-colors"><UsersIcon className="w-4 h-4" /></button>
            </div>
          </div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/60" />
            <input data-testid="chat-search" className={`${inputCls} pl-9`} placeholder="Buscar conversa..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {filtered.length === 0 && <p className="text-sm text-muted-foreground/70 text-center py-10 px-4">Nenhuma conversa ainda. Clique em + para começar.</p>}
          {filtered.map((c) => (
            <button key={c.id} data-testid={`conv-${c.id}`} onClick={() => { setActive(c); }} className={`w-full flex items-center gap-3 px-4 py-3.5 text-left border-b border-border/70 hover:bg-muted/40 transition-colors ${active?.id === c.id ? 'bg-muted/50' : ''}`}>
              {c.type === 'group'
                ? <div className="w-10 h-10 rounded-full bg-muted/50 border border-border flex items-center justify-center shrink-0"><UsersIcon className="w-4.5 h-4.5 text-foreground/65" /></div>
                : <Avatar name={c.title} online={c.online} />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-sm truncate">{c.title}</span>
                  <span className="text-[10px] text-muted-foreground/60 shrink-0">{fmtTime(c.last_message_at)}</span>
                </div>
                <div className="flex items-center justify-between gap-2 mt-0.5">
                  <span className="text-xs text-muted-foreground truncate">{c.last_message ? `${c.last_message.sender_name?.split(' ')[0]}: ${c.last_message.text}` : 'Conversa iniciada'}</span>
                  {c.unread > 0 && <span data-testid={`unread-${c.id}`} className="bg-primary text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center shrink-0">{c.unread}</span>}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Thread */}
      <div className={`flex-1 flex-col ${active ? 'flex' : 'hidden md:flex'}`}>
        {!active ? (
          <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground/60 gap-3">
            <MessagesSquare className="w-12 h-12" />
            <p className="text-sm">Selecione uma conversa ou inicie uma nova</p>
          </div>
        ) : (
          <>
            <div className="px-5 py-3.5 border-b border-border/70 flex items-center gap-3">
              <button className="md:hidden text-muted-foreground mr-1" onClick={() => setActive(null)}>←</button>
              {active.type === 'group'
                ? <div className="w-9 h-9 rounded-full bg-muted/50 border border-border flex items-center justify-center"><UsersIcon className="w-4 h-4 text-foreground/65" /></div>
                : <Avatar name={active.title} online={convs.find((c) => c.id === active.id)?.online} size={36} />}
              <div>
                <p className="font-semibold text-sm" data-testid="thread-title">{active.title}</p>
                <p className="text-[11px] text-muted-foreground">
                  {active.type === 'group'
                    ? `${(convs.find((c) => c.id === active.id)?.members?.length || 0) + 1} participantes`
                    : (convs.find((c) => c.id === active.id)?.online ? <span className="text-emerald-400">● Online</span> : 'Offline')}
                </p>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3" data-testid="messages-list">
              {messages.map((m) => {
                const mine = m.sender_id === me?.user_id;
                return (
                  <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${mine ? 'bg-primary rounded-br-sm' : 'bg-muted/50 border border-border/70 rounded-bl-sm'}`}>
                      {!mine && active.type === 'group' && <p className="text-[11px] font-bold text-primary mb-0.5">{m.sender_name}</p>}
                      {m.attachment ? (
                        m.attachment.content_type?.startsWith('image/') ? (
                          <a href={`${API}/files/${m.attachment.path}`} target="_blank" rel="noreferrer">
                            <img src={`${API}/files/${m.attachment.path}`} alt={m.attachment.original_filename} className="rounded-lg max-h-[220px] max-w-full object-contain" data-testid={`chat-img-${m.id}`} />
                          </a>
                        ) : (
                          <a href={`${API}/files/${m.attachment.path}`} target="_blank" rel="noreferrer" data-testid={`chat-file-${m.id}`} className={`flex items-center gap-2 text-sm underline-offset-2 hover:underline ${mine ? 'text-foreground' : 'text-primary'}`}>
                            <FileText className="w-4 h-4 shrink-0" /> {m.attachment.original_filename || 'arquivo'}
                          </a>
                        )
                      ) : (
                        <p className="text-sm whitespace-pre-wrap break-words">{m.text}</p>
                      )}
                      <p className={`text-[10px] mt-1 ${mine ? 'text-foreground/60' : 'text-muted-foreground/60'} text-right`}>{fmtTime(m.created_at)}</p>
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>
            <div className="p-4 border-t border-border/70 flex gap-3">
              <input ref={fileRef} type="file" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip,.mp4" multiple className="hidden" onChange={(e) => { sendFiles(Array.from(e.target.files)); e.target.value = ''; }} />
              <button data-testid="chat-attach-btn" onClick={() => fileRef.current?.click()} disabled={uploading} title="Anexar imagem, PDF ou arquivo" className="px-3.5 rounded-lg bg-muted/50 border border-border hover:bg-muted/80 disabled:opacity-40 transition-colors">{uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}</button>
              <input data-testid="message-input" className={inputCls} placeholder="Escreva uma mensagem..." value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && send()} />
              <button data-testid="send-message-btn" onClick={send} disabled={sending || !text.trim()} className="px-4 rounded-lg bg-primary hover:bg-primary/90 disabled:opacity-40 transition-colors"><Send className="w-4 h-4" /></button>
            </div>
          </>
        )}
      </div>

      {/* Dialog nova conversa / grupo */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent aria-describedby={undefined} className="bg-card border-border text-foreground">
          <DialogHeader><DialogTitle className="font-display font-black text-xl uppercase">{mode === 'dm' ? 'Nova Conversa' : 'Novo Grupo'}</DialogTitle></DialogHeader>
          {mode === 'group' && (
            <div><label className="text-xs text-muted-foreground mb-1.5 block">Nome do grupo</label><input data-testid="group-name-input" className={inputCls} value={groupName} onChange={(e) => setGroupName(e.target.value)} placeholder="Ex: Equipe de Vendas" /></div>
          )}
          <div className="max-h-[300px] overflow-y-auto space-y-1 py-1">
            {allUsers.length === 0 && <p className="text-sm text-muted-foreground/70 text-center py-6">Nenhum outro usuário cadastrado.</p>}
            {allUsers.map((u) => (
              <button key={u.user_id} data-testid={`pick-user-${u.login || u.user_id}`}
                onClick={() => mode === 'dm' ? createConv([u.user_id], 'dm') : setSelected((p) => p.includes(u.user_id) ? p.filter((x) => x !== u.user_id) : [...p, u.user_id])}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${selected.includes(u.user_id) ? 'bg-primary/20 border border-primary/40' : 'hover:bg-muted/40 border border-transparent'}`}>
                <Avatar name={u.name} size={32} />
                <div className="flex-1">
                  <p className="text-sm font-medium">{u.name}</p>
                  <p className="text-[11px] text-muted-foreground">{u.role}{u.login ? ` · ${u.login}` : ''}</p>
                </div>
                {mode === 'group' && <span className={`w-4 h-4 rounded border ${selected.includes(u.user_id) ? 'bg-primary border-primary' : 'border-foreground/25'}`} />}
              </button>
            ))}
          </div>
          {mode === 'group' && (
            <DialogFooter>
              <button onClick={() => setNewOpen(false)} className="px-4 py-2.5 rounded-lg text-sm font-medium border border-border text-foreground/65 hover:text-foreground transition-colors">Cancelar</button>
              <button data-testid="create-group-btn" onClick={() => createConv(selected, 'group', groupName)} disabled={!groupName.trim() || selected.length === 0} className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-primary hover:bg-primary/90 disabled:opacity-50 transition-colors">Criar Grupo</button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Chat;
